/**
 * The code is based on 'ng2-file-upload' library (https://github.com/valor-software/ng2-file-upload)
 *
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 Dmitriy Shekhovtsov <valorkin@gmail.com>
 * Copyright (c) 2015 Valor Software
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import {
    Directive,
    ElementRef,
    EventEmitter,
    HostListener,
    inject,
    Input,
    Output
} from "@angular/core";
import { FileUploader, FileUploaderOptions } from "../models";

/**
 * Directive to detect file(s) drag-n-drop events on html drop areas.
 */
@Directive({
    selector: "[lgFileDrop]"
})
export class FileDropDirective {
    protected element = inject(ElementRef);

    /**
     * File uploader class.
     */
    @Input()
    uploader: FileUploader = new FileUploader({ url: "" });

    /**
     * Emits during 'over' and 'out' events for drop area.
     * Returns `true` if file is over Drop Area, `false` in case of out.
     */
    @Output()
    readonly fileOver = new EventEmitter<boolean>();

    /**
     * Emits list of files that have been dropped on a drop area.
     */
    @Output()
    readonly fileDrop = new EventEmitter<FileList>();

    @HostListener("dragover", ["$event"])
    onDragOver(event: DragEvent): void {
        const transfer = this._getTransfer(event);
        if (!this._hasFiles(transfer?.types)) {
            return;
        }

        transfer!.dropEffect = "copy";
        this._preventAndStop(event);
        this.fileOver.emit(true);
    }

    @HostListener("dragleave", ["$event"])
    onDragLeave(event: DragEvent): void {
        this._preventAndStop(event);
        this.fileOver.emit(false);
    }

    @HostListener("drop", ["$event"])
    onDrop(event: DragEvent): void {
        const transfer = this._getTransfer(event);
        if (!transfer) {
            return;
        }

        const options = this.getOptions();
        const filters = this.getFilters();
        this._preventAndStop(event);
        if (options) {
            this.uploader.addToQueue(transfer.files, options, filters);
        }
        this.fileOver.emit(false);
        this.fileDrop.emit(transfer.files);
    }

    getFilters(): string {
        return "";
    }

    getOptions(): FileUploaderOptions | void {
        return this.uploader.options;
    }

    protected _getTransfer(event: DragEvent): DataTransfer | null {
        return event.dataTransfer ?? null;
    }

    protected _hasFiles(types: undefined | null | readonly string[]): boolean {
        return types?.indexOf("Files") !== -1 || false;
    }

    protected _preventAndStop(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();
    }
}
